import { useLocation, useNavigate, useNavigationType, useParams } from 'react-router-dom';
import { useGetSingleCourseQuery } from '../../../../../reduxToolkit/reducers/usersSlice';
import { useEffect, useState } from 'react';
import Breadcrumbs from '../../../../../coreComponents/breadcrumbs';
import { Button, Card, Col, Container, Row } from 'reactstrap';
import { CourseLessonDetailsData, LessonFilters } from '../../../../../types/Courses';
import Loader from '../../../../../layout/Loader';
import LearningListContainer from '../../../../miscellaneous/learning/learningList';
import { CourseCategory, CourseCategoryTranslations } from '../../../../../types/Tables';
import { useAppSelector } from '../../../../../reduxToolkit/hooks';
import { useTranslation } from 'react-i18next';
import CommonCardHeader from '../../../../../coreComponents/CommonCardHeader';
import { baseLang } from '../../../../../lib/utils';

const StudentCourseDetails = () => {
  const { t, i18n } = useTranslation();
  const pageLang = baseLang(i18n.language);
  const { id } = useParams<{ id: string }>();
  const { id: studentId, role } = useAppSelector((state) => state.auth);
  const [scrollPos, setScrollPos] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const navType = useNavigationType();
  const isMyCourses = location.pathname.includes('my-courses');
  const { data, isLoading, isSuccess, status } = useGetSingleCourseQuery(
    isMyCourses ? { studentId, role, id } : { id },
  );
  const [filteredData, setFilteredData] = useState<CourseLessonDetailsData>({
    course: {
      id: 0,
      name: '',
      description: '',
      courseImage: '',
      createdAt: '',
    },
    categories: [],
  });

  useEffect(() => {
    if (!isSuccess) return;

    const target = location.state?.restoreY;
    if (typeof target !== 'number') return;

    let cancelled = false;
    let tries = 30;

    const tick = () => {
      if (cancelled) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const y = Math.min(target, max);
      window.scrollTo(0, y);

      if (tries-- > 0 && Math.abs(window.scrollY - y) > 2) {
        requestAnimationFrame(tick); // keep trying as content renders
      } else {
        // clear route state so it doesn't re-trigger
        navigate('.', { replace: true, state: {} });
      }
    };

    requestAnimationFrame(tick);
    return () => {
      cancelled = true;
    };
  }, [isSuccess, location.key, navigate]);
  useEffect(() => {
    // SSR guard (Next.js etc.)
    if (typeof window === 'undefined') return;

    const onScroll = () => {
      setScrollPos(window.scrollY ?? window.pageYOffset);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // initialize

    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const [categoriesFilterSelectors, setCategoriesFilterSelectors] = useState<{
    categories: {
      filterName: string;
      filterSelectors: { id: number; name: string; text: string; type: string }[];
    };
  }>({
    categories: { filterName: 'Categories', filterSelectors: [] },
  });
  const [durationFilterSelectors, setDurationFilterSelectors] = useState<{
    duration: {
      filterName: string;
      filterSelectors: { id: number; name: string; text: string; type: string }[];
    };
  }>({
    duration: {
      filterName: t('filters.filterDuration'),
      filterSelectors: [
        {
          id: 0,
          name: 'Less than 10 minutes',
          text: t('filters.filterSelector_4', { minutes: 10 }),
          type: 'checkbox',
        },
        {
          id: 1,
          name: '10 to 30 minutes',
          text: t('filters.filterSelector_5', { fromMinutes: 10, toMinutes: 30 }),
          type: 'checkbox',
        },
        {
          id: 2,
          name: 'More than 30 minutes',
          text: t('filters.filterSelector_6', { minutes: 30 }),
          type: 'checkbox',
        },
      ],
    },
  });
  const [currentCourseName, setCurrentCourseName] = useState('');
  useEffect(() => {
    setDurationFilterSelectors({
      duration: {
        filterName: t('filters.filterDuration'),
        filterSelectors: [
          {
            id: 0,
            name: 'Less than 10 minutes',
            text: t('filters.filterSelector_4', { minutes: 10 }),
            type: 'checkbox',
          },
          {
            id: 1,
            name: '10 to 30 minutes',
            text: t('filters.filterSelector_5', { fromMinutes: 10, toMinutes: 30 }),
            type: 'checkbox',
          },
          {
            id: 2,
            name: 'More than 30 minutes',
            text: t('filters.filterSelector_6', { minutes: 30 }),
            type: 'checkbox',
          },
        ],
      },
    });
    if (data) {
      if (pageLang === 'en') {
        setCurrentCourseName(data?.course.name);
      } else {
        const translation = data.course.translations.find(
          (tr: CourseCategoryTranslations) => tr.lang === pageLang,
        );
        setCurrentCourseName(translation?.name);
      }
    }
  }, [data, i18n.language]);

  const [filters, setFilters] = useState<LessonFilters>({
    category: [],
    search: '',
    status: [],
    duration: [],
  });

  useEffect(() => {
    if (isSuccess && status === 'fulfilled') {
      const categories = data.course.categories.map((cat: CourseCategory) => {
        const text_uk = cat.translations?.find((el) => el.lang === 'uk');
        const text_ru = cat.translations?.find((el) => el.lang === 'ru');
        let translatedName = '';

        if (pageLang === 'ru') {
          translatedName = text_ru?.name ? text_ru?.name : cat.name;
        } else if (pageLang === 'uk') {
          translatedName = text_uk?.name ? text_uk?.name : cat.name;
        } else translatedName = cat.name;
        return {
          id: cat.id,
          name: cat.name,
          text: translatedName,

          type: 'checkbox',
        };
      });
      const uniqueCategories = Array.from(
        new Set(categories.map((cat: CourseCategory) => cat.id)),
      ).map((id) => categories.find((cat: CourseCategory) => cat.id === id)!);
      setCategoriesFilterSelectors((prev) => ({
        ...prev,
        categories: {
          filterName: t('filters.filterCategories'),
          filterSelectors: uniqueCategories,
        },
      }));
    }
  }, [data, isSuccess, i18n.language]);
  useEffect(() => {
    if (!data) {
      setFilteredData({
        course: {
          id: 0,
          name: '',
          description: '',
          courseImage: '',
          createdAt: '',
        },
        categories: [],
      });
      return;
    }
    if (data.course.categories.length > 0) {
      const filteredCategories = data.course.categories
        .filter(
          (cat: CourseCategory) =>
            filters.category.length === 0 || filters.category.includes(cat.id),
        )
        .map((cat: CourseCategory) => {
          const filteredLessons = cat.lessons
            .filter((lesson) => {
              const searchLower = filters.search.toLowerCase();
              const matchesSearch =
                !filters.search || lesson.title.toLowerCase().includes(searchLower);

              const lessonStatus = lesson.studentScores?.[0]?.status as
                | 'in_progress'
                | 'completed'
                | undefined;
              const matchesStatus =
                filters.status.length === 0 ||
                (lessonStatus && filters.status.includes(lessonStatus));

              const length = lesson.video?.length || 0;
              const matchesDuration =
                filters.duration.length === 0 ||
                filters.duration.some((range) => {
                  if (range === 0) return length < 10 * 60;
                  if (range === 1) return length >= 10 * 60 && length < 30 * 60;
                  if (range === 2) return length >= 30 * 60;
                  return false;
                });

              return matchesSearch && matchesStatus && matchesDuration;
            })
            .sort((a, b) => (a.lesson_pos ?? a.id) - (b.lesson_pos ?? b.id));

          return { ...cat, lessons: filteredLessons };
        })
        .filter((cat: CourseCategory) => cat.lessons.length > 0);
      setFilteredData({
        course: data.course,
        categories: filteredCategories,
      });
    }
  }, [data, filters, i18n.language]);

  const pageParents = [
    {
      name: t('sidebar.myCourses'),
      link: '/courses/my-courses',
    },
  ];

  return (
    <>
      {/* {isLoading && <Loader />} */}
      {isSuccess && status === 'fulfilled' && (
        <>
          <Breadcrumbs
            mainTitle={currentCourseName}
            // parent={t('courses.generalTitle')}
            parentsArray={pageParents}
          />
          <Container fluid>
            <Row>
              <Col sm="12">
                <Card>
                  <CommonCardHeader
                    title={t('students.studentsCoursesListCardTitle', {
                      courseName: filteredData.course?.name,
                    })}
                    tag
                    tagClass="pb-0"
                  />

                  <Col sm="3" className="mb-3 px-3">
                    {' '}
                    <Button color="primary" onClick={() => navigate(`/courses/my-courses`)}>
                      ← {t('global.backToCoursesList')}
                    </Button>
                  </Col>
                </Card>
              </Col>
            </Row>

            <LearningListContainer
              courseLessonsData={filteredData}
              filters={filters}
              setFilters={setFilters}
              categoriesFilterSelectors={categoriesFilterSelectors}
              durationFilterSelectors={durationFilterSelectors}
              status={filters.status}
            />
          </Container>
        </>
      )}
    </>
  );
};
export default StudentCourseDetails;
